// /settings — account preferences.
//
// Sections (v1):
//   - Theme: System / Light / Dark. Same writes that ThemeToggle does,
//     surfaced here as an explicit choice list for users who want to set
//     once rather than discover the cycling icon.
//   - Privacy: default "hide my identity from agent" for profile_views +
//     film_plays. Per-view overrides still win at the endpoint layer;
//     this is the persistent default.
//
// Anonymous viewers get a short prompt to sign in (the underlying
// endpoints would 401 anyway).

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiFetch, apiJson } from '../lib/api'
import { useSession } from '../lib/auth'
import { useTheme, type Theme } from '../hooks/useTheme'

interface MeResponse {
  profile: {
    default_anonymous_views: boolean
  }
}

const THEME_OPTIONS: Array<{ value: Theme; label: string; helper: string }> = [
  { value: 'system', label: 'System', helper: 'Match your OS setting' },
  { value: 'light', label: 'Light', helper: 'Always light' },
  { value: 'dark', label: 'Dark', helper: 'Always dark' },
]

export default function Settings() {
  const navigate = useNavigate()
  const { data: session, isPending: sessionPending } = useSession()
  const { theme, setTheme } = useTheme()

  const [anonymousDefault, setAnonymousDefault] = useState<boolean | null>(null)
  const [privacyLoading, setPrivacyLoading] = useState(true)
  const [privacySaving, setPrivacySaving] = useState(false)
  const [privacyError, setPrivacyError] = useState<string | null>(null)

  useEffect(() => {
    if (sessionPending) return
    if (!session?.user) {
      setPrivacyLoading(false)
      return
    }
    let cancelled = false
    setPrivacyLoading(true)
    setPrivacyError(null)
    apiJson<MeResponse>('/api/me')
      .then((data) => {
        if (cancelled) return
        setAnonymousDefault(data.profile.default_anonymous_views)
      })
      .catch((e: unknown) => {
        if (!cancelled) setPrivacyError(e instanceof Error ? e.message : 'load_failed')
      })
      .finally(() => {
        if (!cancelled) setPrivacyLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [session, sessionPending])

  async function setAnonymous(next: boolean) {
    setAnonymousDefault(next) // optimistic
    setPrivacySaving(true)
    setPrivacyError(null)
    try {
      const res = await apiFetch('/api/me/privacy', {
        method: 'PUT',
        body: JSON.stringify({ default_anonymous_views: next }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => null) as { error?: string } | null
        throw new Error(body?.error ?? `request_failed_${res.status}`)
      }
    } catch (e: unknown) {
      // Rollback optimistic update.
      setAnonymousDefault(!next)
      setPrivacyError(e instanceof Error ? e.message : 'save_failed')
    } finally {
      setPrivacySaving(false)
    }
  }

  if (sessionPending) {
    return (
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <p className="text-hoop-text-3 text-sm">Loading…</p>
      </main>
    )
  }

  if (!session?.user) {
    return (
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 text-center">
        <h1 className="text-2xl font-bold text-hoop-text mb-3">
          Settings
        </h1>
        <p className="text-hoop-text-2 text-sm mb-6">
          Sign in to manage your preferences.
        </p>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="bg-hoop-primary text-hoop-primary-fg px-6 py-2 rounded-md text-sm font-medium"
        >
          Sign in
        </button>
      </main>
    )
  }

  return (
    <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-8">
      <header>
        <h1 className="text-2xl font-bold text-hoop-text">
          Settings
        </h1>
        <p className="text-sm text-hoop-text-3 mt-1">
          {session.user.email}
        </p>
      </header>

      <Section title="Theme" description="Choose how HoopRoster appears in your browser.">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {THEME_OPTIONS.map((opt) => {
            const active = opt.value === theme
            return (
              <button
                key={opt.value}
                type="button"
                aria-pressed={active}
                onClick={() => void setTheme(opt.value)}
                className={
                  'text-left rounded-lg border px-4 py-3 transition-colors ' +
                  (active
                    ? 'border-hoop-primary bg-hoop-primary text-hoop-primary-fg'
                    : 'border-hoop-border bg-hoop-surface text-hoop-text-2 hover:border-hoop-accent')
                }
              >
                <div className="font-medium text-sm">{opt.label}</div>
                <div className={'text-xs mt-0.5 ' + (active ? 'opacity-80' : 'text-hoop-text-3')}>
                  {opt.helper}
                </div>
              </button>
            )
          })}
        </div>
      </Section>

      <Section
        title="Privacy"
        description="Control whether agents see your identity when you view a player or film."
      >
        {privacyError && (
          <div className="border border-hoop-red-bg bg-hoop-red-bg text-hoop-red rounded-md px-4 py-3 text-sm mb-3">
            {privacyError}
          </div>
        )}
        {privacyLoading || anonymousDefault === null ? (
          <p className="text-hoop-text-3 text-sm">Loading…</p>
        ) : (
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={anonymousDefault}
              disabled={privacySaving}
              onChange={(e) => void setAnonymous(e.target.checked)}
              className="mt-1 rounded border-hoop-border accent-hoop-primary disabled:opacity-50"
            />
            <span className="text-sm">
              <span className="font-medium text-hoop-text">
                Hide my identity from agents by default
              </span>
              <span className="block text-xs text-hoop-text-3 mt-0.5">
                When enabled, agents see views and film plays from you as
                anonymous. You can override this on individual views in the
                future.
              </span>
            </span>
          </label>
        )}
      </Section>

      <Section title="Account" description="Other account controls.">
        <div className="text-sm text-hoop-text-2">
          {/* mailto, not an in-app route — there is no support surface yet.
              INTERIM: namd@cedarparkventures.ai until hooproster.com email
              routing is set up; then switch to support@hooproster.com. */}
          <p>
            Need to switch role or change email? Email{' '}
            <a
              href="mailto:namd@cedarparkventures.ai?subject=HoopRoster%20account%20help"
              className="underline"
            >
              namd@cedarparkventures.ai
            </a>
            .
          </p>
        </div>
      </Section>
    </main>
  )
}

function Section({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <section className="bg-hoop-surface border border-hoop-border rounded-lg p-5">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-hoop-text">
          {title}
        </h2>
        {description && (
          <p className="text-xs text-hoop-text-3 mt-0.5">
            {description}
          </p>
        )}
      </div>
      {children}
    </section>
  )
}
